import { useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { cleLieux, type FiltresPlanning } from "@/features/planning/api";
import { PlanningPage } from "@/features/planning/PlanningPage";
import type { VuePlanning } from "@/features/planning/PlanningCalendar";

// Vue, période et filtres vivent dans l'URL : un rechargement ou un lien partagé
// retombe sur le même écran.
const recherchePlanning = z.object({
  vue: z.enum(["jour", "semaine", "mois"]).optional().catch(undefined),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
  // Plusieurs lieux : « 12,21 ».
  lieux: z.string().regex(/^\d+(,\d+)*$/).optional().catch(undefined),
  coach: z.coerce.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute("/_connecte/planning")({
  validateSearch: recherchePlanning,
  component: Planning,
});

function aujourdhui(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function Planning() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const onNavigation = useCallback(
    (vue: VuePlanning, date: string) =>
      navigate({ search: (s) => ({ ...s, vue, date }), replace: true }),
    [navigate],
  );

  const onFiltres = useCallback(
    (f: FiltresPlanning) =>
      navigate({ search: (s) => ({ ...s, lieux: cleLieux(f.idLieux) || undefined, coach: f.idCoach }) }),
    [navigate],
  );

  return (
    <PlanningPage
      vue={search.vue ?? "semaine"}
      date={search.date ?? aujourdhui()}
      filtres={{ idLieux: search.lieux?.split(",").map(Number), idCoach: search.coach }}
      onNavigation={onNavigation}
      onFiltres={onFiltres}
    />
  );
}
